<template>
  <v-dialog :retain-focus="false" :value="sectionEditor.show" :width="1000" @input="handleClosePopup">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
          <span style="color: #0081c7">{{ fields.title }}</span>
          <v-btn icon @click="handleClosePopup">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
      </div>

      <v-card-text style="max-height: 70vh; overflow-y: auto">
        <div class="mb-4">
          <label class="label-input">Section</label>
          <v-row>
            <v-col cols="12" md="8">
              <v-text-field v-model="fields.title" label="Name" hide-details />
            </v-col>
            <v-col cols="12" md="4">
              <v-checkbox v-model="fields.enableAnchor" label="Enable Anchor" hide-details />
            </v-col>
          </v-row>
        </div>

        <div class="mb-4">
          <SectionSettingsContainerFuild
            :settings="fields.settings"
            :container-page="fields.containerPage"
            :use-background-mobile="true"
          />
        </div>

        <div class="mb-4">
          <label class="label-input">Header</label>
          <div class="group-item">
            <ckeditor :editor="editor" v-model="fields.header" :config="editorConfig" />
          </div>
        </div>

        <div class="mb-4">
          <label class="label-input">Tag: 3 Brightness Levels</label>
          <div class="group-item">
            <v-checkbox v-model="fields.tags.brightness.show" label="Show tag" hide-details dense class="mb-2" />
            <v-text-field v-model="fields.tags.brightness.text" label="Text" hide-details class="mb-4" />
            <UploadImage
              @callback="handleUploadTagBrightnessBg"
              :image="{ imageSrc: fields.tags.brightness.bgImage }"
              :height="`80`"
              @callbackDelete="handleRemoveTagBrightnessBg"
            />
          </div>
        </div>

        <div class="mb-4">
          <label class="label-input">Tag: 10 High-CRI LED Lights</label>
          <div class="group-item">
            <v-checkbox v-model="fields.tags.led.show" label="Show tag" hide-details dense class="mb-2" />
            <v-text-field v-model="fields.tags.led.text" label="Text" hide-details class="mb-4" />
            <UploadImage
              @callback="handleUploadTagLedBg"
              :image="{ imageSrc: fields.tags.led.bgImage }"
              :height="`80`"
              @callbackDelete="handleRemoveTagLedBg"
            />
          </div>
        </div>

        <div class="mb-4">
          <label class="label-input">Brightness Boxes</label>
          <v-expansion-panels accordion>
            <v-expansion-panel v-for="(item, index) in fields.boxes" :key="`sowhatbuy-card-image-box-${index}`">
              <v-expansion-panel-header> {{ index + 1 }}. {{ item.label || 'Box' }} </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-text-field v-model="item.label" label="Label" hide-details class="mb-0" />
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>
      </v-card-text>

      <div class="footer-modal">
        <v-card-actions style="padding: 10px 0">
          <v-spacer></v-spacer>
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import UploadImage from '@/components/form/uploadFile/UpdateImage.vue';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { updateHtmlFromData } from '../common/html/update';
import SectionSettingsContainerFuild from './common/section-settings-container-fuild.vue';

export default {
  name: 'sowhatbuy-card-image-section-editor',
  components: {
    UploadImage,
    SectionSettingsContainerFuild,
  },
  props: {
    sectionEditor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      fields: {
        title: 'Sowhatbuy card image',
        enableAnchor: false,
        settings: {},
        containerPage: {},
        header: '',
        tags: {
          brightness: {
            show: true,
            text: '3 Brightness Levels',
            bgImage: 'https://sowhatbuy.com/cdn/shop/files/20260331-p4-1-pc.png',
          },
          led: {
            show: true,
            text: '10 High-CRI LED Lights',
            bgImage: 'https://sowhatbuy.com/cdn/shop/files/20260331-p4-2-pc.png',
          },
        },
        boxes: [{ label: 'Low' }, { label: 'Medium' }, { label: 'High' }],
      },
    };
  },
  watch: {
    sectionEditor: {
      immediate: true,
      deep: true,
      handler(val) {
        if (val?.show && val.sectionExtract) {
          this.applyExtract(val.sectionExtract);
        }
      },
    },
  },
  methods: {
    applyExtract(extract) {
      if (!extract) return;
      this.fields = {
        ...this.fields,
        ...extract,
        settings: { ...this.fields.settings, ...(extract.settings || {}) },
        containerPage: { ...this.fields.containerPage, ...(extract.containerPage || {}) },
        tags: {
          brightness: {
            ...this.fields.tags.brightness,
            ...((extract.tags || {}).brightness || {}),
          },
          led: {
            ...this.fields.tags.led,
            ...((extract.tags || {}).led || {}),
          },
        },
        boxes: Array.isArray(extract.boxes) ? extract.boxes.map(item => ({ ...item })) : this.fields.boxes,
      };
    },
    handleClosePopup() {
      this.$emit('close');
    },
    handleUploadTagBrightnessBg(obj) {
      if (obj?.imageSrc) this.fields.tags.brightness.bgImage = obj.imageSrc;
    },
    handleRemoveTagBrightnessBg() {
      this.fields.tags.brightness.bgImage = '';
    },
    handleUploadTagLedBg(obj) {
      if (obj?.imageSrc) this.fields.tags.led.bgImage = obj.imageSrc;
    },
    handleRemoveTagLedBg() {
      this.fields.tags.led.bgImage = '';
    },
    handleSave() {
      const updateHtml = updateHtmlFromData.sectionSowhatbuyCardImage(this.sectionEditor.section.html, this.fields);
      this.$emit('update:section', {
        ...this.sectionEditor.section,
        html: updateHtml,
        title: this.fields.title,
      });
      this.handleClosePopup();
    },
  },
};
</script>
